import { FaWhatsapp, FaFacebook, FaInstagram, FaTiktok, FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowUp } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { useContactData, useFooterData } from '../hooks/useTranslations';
import { useTheme } from '../contexts/ThemeContext';
import contactConfig from '../config/contact';

const Footer = () => {
    const { t, language } = useLanguage();
    const contactData = useContactData();
    const footerData = useFooterData();
    const { isDark } = useTheme();

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 80;
            const elementPosition = element.offsetTop - navbarHeight;
            window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <footer className={`relative ${isDark
            ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black'
            : 'bg-gradient-to-br from-saratoga via-atlantis to-green-yellow-primary'
            }`}>
            {/* Decorative top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-atlantis via-green-yellow-primary to-vida-loca"></div>

            <div className="max-w-7xl mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* Logo y descripción */}
                    <div className="lg:col-span-2">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                <img
                                    src="/logo.jpg"
                                    alt="NJ Design & Print"
                                    className="w-8 h-8 rounded-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">NJ Design & Print</h3>
                                <p className="text-white/80 text-sm">{t.heroSubtitle}</p>
                            </div>
                        </div>
                        <p className="text-white/90 text-sm leading-relaxed mb-6 max-w-md">
                            {footerData.footerDesc || t.aboutText1}
                        </p>

                        {/* Redes sociales */}
                        <div className="flex space-x-4">
                            <a
                                href={contactConfig.social.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-[#25d366] hover:bg-[#20b858] text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                                aria-label="WhatsApp"
                            >
                                <FaWhatsapp className="text-lg" />
                            </a>
                            <a
                                href="https://www.instagram.com/njdesignprint/profilecard/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-gradient-to-r from-[#E4405F] to-[#C13584] hover:from-[#d12e47] hover:to-[#a8296e] text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                                aria-label="Instagram"
                            >
                                <FaInstagram className="text-lg" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@design.print1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-[#000] hover:bg-[#333] text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                                aria-label="TikTok"
                            >
                                <FaTiktok className="text-lg" />
                            </a>
                            <a
                                href="https://www.facebook.com/NJdesignandPrint/?rdid=0belVwxUyRmtedo9"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-[#1877f3] hover:bg-[#166fe5] text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                                aria-label="Facebook"
                            >
                                <FaFacebook className="text-lg" />
                            </a>
                        </div>
                    </div>

                    {/* Enlaces rápidos */}
                    <div>
                        <h4 className="text-lg font-semibold text-white mb-4">{footerData.quickLinks || 'Enlaces Rápidos'}</h4>
                        <ul className="space-y-2">
                            <li>
                                <button
                                    onClick={() => scrollToSection('home')}
                                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm"
                                >
                                    {t.home}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollToSection('about')}
                                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm"
                                >
                                    {t.about}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollToSection('services')}
                                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm"
                                >
                                    {t.services}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollToSection('contact')}
                                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm"
                                >
                                    {t.contact}
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Información de contacto */}
                    <div>
                        <h4 className="text-lg font-semibold text-white mb-4">{t.contactInfo}</h4>
                        <ul className="space-y-3">
                            <li className="flex items-start space-x-3">
                                <FaPhone className="text-white/80 text-sm mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/90 text-sm font-medium">{contactData.details.phoneNumber}</p>
                                    <p className="text-white/70 text-xs">{t.mondayToFriday}</p>
                                </div>
                            </li>
                            <li className="flex items-start space-x-3">
                                <FaEnvelope className="text-white/80 text-sm mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/90 text-sm font-medium">{contactData.details.emailAddress}</p>
                                    <p className="text-white/70 text-xs">{t.quickResponse}</p>
                                </div>
                            </li>
                            <li className="flex items-start space-x-3">
                                <FaMapMarkerAlt className="text-white/80 text-sm mt-1 flex-shrink-0" />
                                <div>
                                    <p className="text-white/90 text-sm font-medium">{contactData.details.address}</p>
                                    <p className="text-white/70 text-xs">{t.location}</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Línea divisoria */}
                <div className="border-t border-white/20 my-8"></div>

                {/* Footer bottom */}
                <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                    <div className="text-center md:text-left">
                        <p className="text-white/80 text-sm">
                            © {new Date().getFullYear()} NJ Design & Print LLC (NJ Design&amp;Print). {footerData.allRightsReserved || 'Todos los derechos reservados.'}
                        </p>
                    </div>

                    {/* Botón volver arriba */}
                    <button
                        onClick={scrollToTop}
                        className="group bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300 transform hover:scale-110 shadow-lg"
                        aria-label={language === 'es' ? 'Volver arriba' : 'Back to top'}
                    >
                        <FaArrowUp className="text-white group-hover:text-white transition-colors duration-200" />
                    </button>
                </div>

                {/* Cyber Lab Credit */}
                <div className="border-t border-white/10 mt-6 pt-4">
                    <div className="text-center">
                        <p className="text-white/50 text-xs">
                            {footerData.designedBy || 'Diseñado por'}{' '}
                            <a
                                href="https://cyberlabllc.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/70 hover:text-white transition-colors duration-200 underline decoration-dotted underline-offset-2 hover:decoration-solid"
                            >
                                Cyber Lab Hardware & Software LLC
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            {/* Efecto de gradiente inferior */}
            <div className={`absolute bottom-0 left-0 right-0 h-32 ${isDark
                ? 'bg-gradient-to-t from-black/50 to-transparent'
                : 'bg-gradient-to-t from-marshland/30 to-transparent'
                } pointer-events-none`}></div>
        </footer>
    );
};

export default Footer;
